import {
  IPaginatedResults,
  IThirdPartyNode,
  IThirdPartyPermission,
  ThirdPartyGlobalPermissionRecord,
  ThirdPartyPermission,
  ThirdPartyPermissionRecord,
  ThirdPartyProvider,
} from '@dealpoint/types'
import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { identity, isEqual, omit, orderBy, uniq } from 'lodash'
import { batch } from 'react-redux'

import { APIClient } from '../../APIClient'
import { Status } from '../../types'
import cleanFilename from '../../utils/cleanFilename'
import formatDate from '../../utils/dates/format'
import download from '../../utils/download'
import wait from '../../utils/wait'
import zip from '../../utils/zip'
// eslint-disable-next-line import/no-cycle
import { RootState } from '../types'

import { ExportFormat, NodeId, NodePath, UserId } from './types'
import {
  createGuardPermissions,
  createNodeId,
  getNodeIdParts,
  getParentPermissions,
  globalPermissionGuards,
  globalPermissionSetGuards,
  isAncestor,
  isGlobalNode,
  permissionSetGuards,
} from './utils'

const STATUS_CHANGE_DELAY = 5000

interface IPayloadActionWithUserId {
  userId: UserId
}
type PayloadActionWithUserId<T extends Record<string, any>> = PayloadAction<
  IPayloadActionWithUserId & T
>

interface IPayloadActionWithIdProps extends IPayloadActionWithUserId {
  path: NodePath
  provider: ThirdPartyProvider
}
type PayloadActionWithIdProps<T extends Record<string, any>> = PayloadAction<
  IPayloadActionWithIdProps & T
>

interface IDataPrivacyNodeState {
  byId: Record<NodeId, IThirdPartyNode>
  byParentNode: Record<NodeId, { children: NodeId[]; count: number; pages: number }>
  expanded: NodeId[]
  providers: ThirdPartyProvider[]
  roots: NodeId[]
  selected: NodeId[]
  status: Record<NodeId, Status>
}

interface IDataPrivacyPermissionsState {
  byNodeId: Record<NodeId, ThirdPartyPermissionRecord>
  counts: Record<NodeId, Partial<Record<ThirdPartyPermission, number>>>
  sorted: NodeId[]
  status: Record<NodeId, Status>
  dirty: IThirdPartyPermission[]
}

interface IDataPrivacyState {
  exportStatus: Record<string, Status>
  nodes: Record<UserId, IDataPrivacyNodeState>
  permissions: {
    byUserId: Record<UserId, IDataPrivacyPermissionsState>
    global: Record<UserId, ThirdPartyGlobalPermissionRecord>
    status: Status
    userIds: string[]
  }
  search: Record<UserId, string>
  selectedUserId: string
  status: Record<UserId, Status>
  uploadStatus: Record<
    UserId,
    Partial<Record<ThirdPartyProvider, { percentComplete?: number; status: Status }>>
  >
}

const initialState: IDataPrivacyState = {
  exportStatus: {},
  nodes: {},
  permissions: {
    byUserId: {},
    global: {},
    status: 'init',
    userIds: [],
  },
  search: {},
  selectedUserId: '',
  status: {},
  uploadStatus: {},
}

const slice = createSlice({
  name: 'dataPrivacy',
  initialState,
  reducers: {
    addNodeExpanded(state, action: PayloadAction<IPayloadActionWithIdProps>) {
      const { userId } = action.payload
      const id = createNodeId(action.payload)
      state.nodes[userId].expanded.push(id)
    },
    addNodeSelected(state, action: PayloadAction<IPayloadActionWithIdProps>) {
      const { userId } = action.payload
      const id = createNodeId(action.payload)
      state.nodes[userId].selected.push(id)
    },
    addNodes(
      state,
      action: PayloadAction<
        IPayloadActionWithUserId & {
          count?: number
          limit?: number
          nodes: IThirdPartyNode[]
          page?: number
          pages?: number
        }
      >
    ) {
      const { count, limit = 20, nodes, page = 1, pages, userId } = action.payload
      const { byId, byParentNode, status } = state.nodes[userId]
      nodes.forEach((next, index) => {
        const { parent, provider } = next
        const id = createNodeId(next)
        const previous = state.nodes[userId].byId[id]

        // If the next and previous nodes are the same, dont update
        if (JSON.stringify(previous) === JSON.stringify(next)) {
          return
        }

        // If this provider hasn't been added then add it now
        if (!state.nodes[userId].providers.includes(provider)) {
          state.nodes[userId].providers.push(provider)
        }

        byId[id] = next
        if (parent) {
          const parentId = createNodeId({ provider, path: parent })
          byParentNode[parentId] = byParentNode[parentId] ?? { children: [], count: 0, pages: 1 }

          byParentNode[parentId].children[index + (page - 1) * limit] = id
          byParentNode[parentId].count = count ?? byParentNode[parentId].children.length
          byParentNode[parentId].pages = pages ?? 1
        }
        status[id] = status[id] ?? 'idle'
      })
    },
    initialize(state, action: PayloadAction<string>) {
      const userId = action.payload
      if (!state.nodes[userId]) {
        state.nodes[userId] = initializeNodes()
        state.permissions.byUserId[userId] = initializePermissions()
        state.search[userId] = ''
        state.status[userId] = 'init'
      }
    },
    removeNodeChildren(state, action: PayloadActionWithIdProps<{ recursive?: boolean }>) {
      const { recursive, userId } = action.payload
      const { byId, byParentNode, expanded, status } = state.nodes[userId]
      const id = createNodeId(action.payload)

      const getRemoveIds = (id) => {
        const childIds = byParentNode[id]?.children ?? []
        return recursive ? childIds.concat(childIds.flatMap((id) => getRemoveIds(id))) : childIds
      }

      const removeIds = getRemoveIds(id)
      state.nodes[userId].byParentNode = omit(byParentNode, [id].concat(removeIds))
      state.nodes[userId].byId = omit(byId, removeIds)
      state.nodes[userId].expanded = expanded.filter((id) => !removeIds.includes(id))
      state.nodes[userId].status = omit(status, removeIds)
    },
    removeNodeExpanded(state, action: PayloadAction<IPayloadActionWithIdProps>) {
      const { userId } = action.payload
      const id = createNodeId(action.payload)
      state.nodes[userId].expanded = state.nodes[userId].expanded.filter(
        (expandedId) => expandedId !== id
      )
    },
    removeNodesExpanded(state, action: PayloadAction<UserId>) {
      state.nodes[action.payload].expanded = []
    },
    removeNodeSelected(state, action: PayloadAction<IPayloadActionWithIdProps>) {
      const { userId } = action.payload
      const id = createNodeId(action.payload)
      state.nodes[userId].selected = state.nodes[userId].selected.filter(
        (selectedId) => selectedId !== id
      )
    },
    setExportStatus(
      state,
      action: PayloadActionWithUserId<{
        path: NodePath
        provider: ThirdPartyProvider
        status: Status
      }>
    ) {
      const { path, provider, status, userId } = action.payload
      state.exportStatus[userId + createNodeId({ path, provider })] = status
    },
    setNodeRoots(state, action: PayloadActionWithUserId<{ ids: NodeId[] }>) {
      const { ids, userId } = action.payload
      state.nodes[userId].roots = ids
    },
    setNodeStatus(state, action: PayloadActionWithIdProps<{ status: Status }>) {
      const { status, userId } = action.payload
      const id = createNodeId(action.payload)
      state.nodes[userId].status[id] = status
    },
    setGlobalPermission(
      state,
      action: PayloadAction<{
        permissions: Partial<ThirdPartyGlobalPermissionRecord>
        userId: UserId
      }>
    ) {
      const { permissions, userId } = action.payload
      const { global, byUserId } = state.permissions
      const { byNodeId } = byUserId[userId]

      const updated = (global[userId] = { ...global[userId], ...permissions })

      const guardPermissions = createGuardPermissions<
        ThirdPartyPermissionRecord,
        ThirdPartyGlobalPermissionRecord
      >(globalPermissionSetGuards, updated)

      // Update permissions appropriately
      Object.entries(byNodeId).forEach(([nodeId, record]) => {
        const updatedRecord = guardPermissions(record, updated)
        if (!isEqual(record, updatedRecord)) {
          byNodeId[nodeId] = updatedRecord
          byUserId[userId].dirty.push({
            ...getNodeIdParts(nodeId),
            permissions: updatedRecord,
          })
        }
      })
    },
    setPermission(
      state,
      action: PayloadActionWithIdProps<{
        permissions: Partial<ThirdPartyPermissionRecord>
      }>
    ) {
      const { permissions, userId } = action.payload
      const id = createNodeId(action.payload)
      const { byNodeId, sorted } = state.permissions.byUserId[userId]
      const globalPermissions = state.permissions.global[userId]

      const changedRecord = { ...byNodeId[id], ...permissions }
      const shouldDelete = !Object.values(changedRecord).filter((v) => v).length
      const shouldSort = shouldDelete || !byNodeId[id]

      const guardPermissions = createGuardPermissions(permissionSetGuards, globalPermissions)

      if (shouldDelete) {
        delete byNodeId[id]
      } else {
        /**
         * Update the given permissions. Guard the updated permissions to ensure it doesn't violate
         * its ancestors.
         */
        const parentPermissions = getParentPermissions(id, byNodeId, globalPermissions)
        byNodeId[id] = guardPermissions(changedRecord, parentPermissions)
      }

      // Update permissions for each descendent permission record
      sorted
        .slice()
        .reverse()
        .reduce((parentRecord, nodeId) => {
          if (isAncestor(nodeId, id)) {
            const currentRecord = byNodeId[nodeId]
            const updatedRecord = guardPermissions(currentRecord, parentRecord)

            if (!isEqual(currentRecord, updatedRecord)) {
              byNodeId[nodeId] = updatedRecord
              state.permissions.byUserId[userId].dirty.push({
                ...getNodeIdParts(nodeId),
                permissions: updatedRecord,
              })

              return updatedRecord
            }

            return currentRecord
          }

          return parentRecord
        }, byNodeId[id])

      // Re-sort byNodeId
      if (shouldSort) {
        state.permissions.byUserId[userId].sorted = orderBy(Object.keys(byNodeId), null, 'desc')
      }

      // Update permission counts
      state.permissions.byUserId[userId].counts = Object.entries(byNodeId).reduce(
        (acc, [id, permissions]) => {
          const { path, provider } = getNodeIdParts(id)
          const newRecords = createPermissionCountRecords(
            provider as ThirdPartyProvider,
            path,
            permissions
          )
          return assignPermissionCounts(acc, newRecords)
        },
        {}
      )
    },
    setPermissions(
      state,
      action: PayloadActionWithUserId<{ permissions: IThirdPartyPermission[] }>
    ) {
      const { userId } = action.payload
      const permissions = action.payload.permissions.filter((p) => !isGlobalNode(p))
      const globalPermission = action.payload.permissions.find((p) =>
        isGlobalNode(p)
      ) as unknown as IThirdPartyPermission<ThirdPartyGlobalPermissionRecord>

      state.permissions.global[userId] =
        globalPermission?.permissions ?? ({} as ThirdPartyGlobalPermissionRecord)

      state.permissions.byUserId[userId].byNodeId = permissions.reduce(
        (acc, p) => ({ ...acc, [createNodeId(p)]: p.permissions }),
        {}
      )

      state.permissions.byUserId[userId].sorted = orderBy(
        permissions.map((p) => createNodeId(p)),
        null,
        'desc'
      )

      // Get count of how many child paths have permissions for each ancestor
      state.permissions.byUserId[userId].counts = permissions.reduce(
        (acc, { path, permissions, provider }) => {
          const newRecords = createPermissionCountRecords(provider, path, permissions)
          return assignPermissionCounts(acc, newRecords)
        },
        {}
      )
    },
    resetPermissionsDirty(state, action: PayloadAction<UserId>) {
      state.permissions.byUserId[action.payload].dirty = []
    },
    setPermissionsOverallStatus(state, action: PayloadAction<Status>) {
      state.permissions.status = action.payload
    },
    setPermissionsStatus(state, action: PayloadActionWithIdProps<{ status: Status }>) {
      const { status, userId } = action.payload
      state.permissions.byUserId[userId].status[createNodeId(action.payload)] = status
    },
    setPermissionsUserIds(state, action: PayloadAction<string[]>) {
      state.permissions.userIds = action.payload
    },
    setSearch(state, action: PayloadActionWithUserId<{ search: string }>) {
      const { search, userId } = action.payload
      state.search[userId] = search
    },
    setSelectedUserId(state, action: PayloadAction<string>) {
      state.selectedUserId = action.payload
    },
    setStatus(state, action: PayloadActionWithUserId<{ status: Status }>) {
      const { status, userId } = action.payload
      state.status[userId] = status
    },
    setUploadStatus(
      state,
      action: PayloadActionWithUserId<{
        percentComplete?: number
        provider: ThirdPartyProvider
        status: Status
      }>
    ) {
      const { percentComplete, provider, status, userId } = action.payload
      const uploadStatus = state.uploadStatus[userId]

      if (!uploadStatus) {
        state.uploadStatus[userId] = { [provider]: { percentComplete, status } }
        return
      }

      const providerUploadStatus = state.uploadStatus[userId][provider]
      if (!providerUploadStatus) {
        state.uploadStatus[userId][provider] = { percentComplete, status }
      }

      state.uploadStatus[userId][provider].percentComplete = percentComplete
      state.uploadStatus[userId][provider].status = status
    },
    updateNodeRoots(state, action: PayloadActionWithUserId<{ ids: NodeId[] }>) {
      const { ids, userId } = action.payload
      state.nodes[userId].roots = uniq(state.nodes[userId].roots.concat(ids))
    },
  },
})

// Helpers

const initializeNodes = (): IDataPrivacyNodeState => ({
  byId: {},
  byParentNode: {},
  expanded: [],
  providers: [],
  roots: [],
  selected: [],
  status: {},
})

const initializePermissions = (): IDataPrivacyPermissionsState => ({
  byNodeId: {},
  counts: {},
  sorted: [],
  status: {},
  dirty: [],
})

const fetchNodes = (
  userId: string,
  provider: ThirdPartyProvider,
  path: NodePath
): Promise<IThirdPartyNode[]> =>
  APIClient.makeAPICall(
    'GET',
    `/data-privacy/nodes/${encodeURIComponent(path)}?provider=${provider}&owner=${userId}`
  )

const updatePermissions = <T = ThirdPartyPermissionRecord>(
  owner: UserId,
  permissions: IThirdPartyPermission<T>[]
): Promise<void> =>
  APIClient.makeAPICall('PUT', `/data-privacy/permissions`, {
    body: { owner, permissions },
  })

const getPermissionCounts = (next: ThirdPartyPermissionRecord) =>
  Object.entries(next).reduce((acc, [type, userIds]) => ({ ...acc, [type]: userIds ? 1 : 0 }), {})

const createPermissionCountRecords = (
  provider: ThirdPartyProvider,
  path: NodePath,
  permissions: ThirdPartyPermissionRecord
) => {
  const ancestory = path.split('/').map((_, index, parts) => parts.slice(0, index + 1).join('/'))
  return ancestory.reduce((acc, path) => {
    const id = createNodeId({ provider, path: path || '/' })
    return {
      ...acc,
      [id]: getPermissionCounts(permissions),
    }
  }, {})
}

const assignPermissionCounts = (
  target: Record<NodeId, Record<ThirdPartyPermission, number>>,
  source: Record<NodeId, Record<ThirdPartyPermission, number>>
) => ({
  ...target,
  ...Object.entries(source).reduce(
    (acc, [id, counts]) => ({
      ...acc,
      [id]: {
        ...acc[id],
        ...Object.entries(counts).reduce(
          (acc, [type, count]) => ({
            ...acc,
            [type]: (acc[type] ?? 0) + count,
          }),
          acc[id] ?? {}
        ),
      },
    }),
    target
  ),
})

const exportResponseHandlers: Record<
  ExportFormat,
  (files: Record<string, unknown>) => Record<string, string>
> = {
  csv: identity,
  json: (files) =>
    Object.entries(files).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: JSON.stringify(value, null, 2) }),
      {}
    ),
}

const createFileName = (files: Record<string, unknown>, format: ExportFormat) => {
  const fileNames = Object.keys(files)
  if (fileNames.length === 1) {
    const parts = fileNames[0].split('/').slice(0, -1)
    return `${parts.join('.')}-${format}-${formatDate(Date.now(), 'yyyyMd')}`
  }

  return `social-data-${format}-${formatDate(Date.now(), 'yyyyMd')}`
}

// Actions
const {
  addNodes,
  removeNodesExpanded,
  resetPermissionsDirty,
  setExportStatus,
  setGlobalPermission,
  setNodeRoots,
  setPermission: _setPermission,
  setPermissions: _setPermissions,
  setPermissionsStatus,
  setPermissionsUserIds,
  setSearch: _setSearch,
  updateNodeRoots,
} = slice.actions

export const {
  addNodeExpanded,
  initialize,
  removeNodeChildren,
  removeNodeExpanded,
  setNodeStatus,
  setPermissionsOverallStatus,
  setSelectedUserId,
  setStatus,
  setUploadStatus,
} = slice.actions

interface AddPermissionedUserProps {
  path: string
  userId: string
  type: ThirdPartyPermission
  provider: ThirdPartyProvider
  owner: string
}

export const addPermissionedUser = createAsyncThunk<void, AddPermissionedUserProps>(
  'dataPrivacy/permissions/addUser',
  async ({ path, userId, type, provider, owner }, { dispatch, getState }) => {
    dispatch(setPermissionsStatus({ path, provider, status: 'saving', userId: owner }))

    const currentUserIds = makeSelectPermission(
      userId,
      provider,
      path,
      type
    )(getState() as RootState)
    const newPermission = { [type]: (currentUserIds ?? []).concat() }

    try {
      dispatch(
        _setPermission({
          path,
          permissions: newPermission,
          provider,
          userId,
        })
      )

      await updatePermissions(userId, [{ path, permissions: newPermission, provider }])

      dispatch(setPermissionsStatus({ path, provider, status: 'saved', userId }))
      setTimeout(
        () => dispatch(setPermissionsStatus({ path, provider, status: 'idle', userId })),
        STATUS_CHANGE_DELAY
      )
    } catch (error) {
      dispatch(setPermissionsStatus({ path, provider, status: 'error', userId }))
    }
  }
)

interface GetChildNodesProps {
  edge?: string | number
  limit: number
  page: number
  path: NodePath
  provider: ThirdPartyProvider
  sort?: 'label' | 'value'
  userId: string
}

export const getNodeChildren = createAsyncThunk(
  'dataPrivacy/nodes/getChildren',
  async (
    { edge, limit, page, path, provider, sort, userId }: GetChildNodesProps,
    { dispatch, getState }
  ) => {
    const { children } = makeSelectChildNodes(
      userId,
      provider,
      path,
      limit,
      page
    )(getState() as RootState)
    const firstTime = !children?.filter((c) => c).length

    firstTime && dispatch(setNodeStatus({ path, provider, status: 'loading', userId }))

    try {
      const query = [
        edge && `edge=${edge}`,
        'limit=20',
        `page=${page}`,
        `provider=${provider}`,
        sort && `sort=${sort}`,
        `owner=${userId}`,
      ].filter((v) => v)
      const results: IPaginatedResults<IThirdPartyNode> = await APIClient.makeAPICall(
        'GET',
        `/data-privacy/nodes/${encodeURIComponent(path)}/children?${query.join('&')}`
      )

      if (firstTime || JSON.stringify(results.data) !== JSON.stringify(children)) {
        dispatch(
          addNodes({
            count: results.totalCount,
            limit,
            nodes: results.data,
            page,
            pages: results.totalPages,
            userId,
          })
        )
      }

      firstTime && dispatch(setNodeStatus({ path, provider, status: 'idle', userId }))
    } catch (error) {
      dispatch(setNodeStatus({ path, provider, status: 'error', userId }))
    }
  }
)

export const getPermissionedUsers = createAsyncThunk(
  'dataPrivacy/permissions/getUsers',
  async (_, { dispatch }) => {
    dispatch(setPermissionsOverallStatus('loading'))
    try {
      const userIds: string[] = await APIClient.makeAPICall(
        'GET',
        '/data-privacy/permissions/users'
      )

      dispatch(setPermissionsUserIds(userIds))
      dispatch(dispatch(setPermissionsOverallStatus('idle')))
    } catch (error) {
      dispatch(setPermissionsOverallStatus('error'))
    }
  }
)

export const getPermissions = createAsyncThunk(
  'dataPrivacy/permissions/get',
  async (userId: UserId, { dispatch, getState }) => {
    const byNodeId = makeSelectPermissionsByNodeId(userId)(getState() as RootState)
    const status = makeSelectStatus(userId)(getState() as RootState)
    const canSetStatus = !Object.keys(byNodeId).length && status !== 'loading'

    canSetStatus && dispatch(setStatus({ status: 'loading', userId }))

    try {
      const permissions = await APIClient.makeAPICall(
        'GET',
        `/data-privacy/permissions?owner=${userId}`
      )
      dispatch(_setPermissions({ permissions, userId }))
      canSetStatus && dispatch(setStatus({ status: 'idle', userId }))
    } catch (error) {
      dispatch(setStatus({ status: 'error', userId }))
    }
  }
)

export const getRootNodes = createAsyncThunk(
  'dataPrivacy/nodes/getRoots',
  async (userId: UserId, { dispatch, getState }) => {
    const isEmpty = makeSelectNodesIsEmpty(userId)(getState() as RootState)
    if (isEmpty) {
      dispatch(initialize(userId))
    }

    const roots = makeSelectNodesRoots(userId)(getState() as RootState)
    !roots.length && dispatch(setStatus({ status: 'loading', userId }))

    try {
      const nodes: IThirdPartyNode[] = await APIClient.makeAPICall(
        'GET',
        `/data-privacy/nodes/${encodeURIComponent('/')}?owner=${userId}`
      )

      if (nodes.length) {
        dispatch(addNodes({ nodes, userId }))
        dispatch(setNodeRoots({ ids: nodes.map(createNodeId), userId }))

        const loadingNodes = nodes.filter(({ loading }) => loading)
        if (loadingNodes.length) {
          loadingNodes.forEach(({ path, provider }) => dispatch(poll({ path, provider, userId })))
        }
      }

      !roots.length && dispatch(setStatus({ status: 'idle', userId }))
    } catch (error) {
      dispatch(setStatus({ status: 'error', userId }))
    }
  }
)

interface GetNodesExportProps {
  format: ExportFormat
  owner: UserId
  path?: NodePath
  provider?: ThirdPartyProvider
}

export const getNodesExport = createAsyncThunk(
  'dataPrivacy/nodes/export',
  async ({ format, owner, path = '/', provider }: GetNodesExportProps, { dispatch }) => {
    const args = { path, provider: provider ?? 'all', userId: owner }
    dispatch(setExportStatus({ ...args, status: 'loading' }))

    const query = [
      `format=${format}&owner=${owner}&recursive=true`,
      provider && `provider=${provider}`,
    ]
      .filter((v) => v)
      .join('&')

    try {
      const files = await APIClient.makeAPICall(
        'GET',
        `/data-privacy/nodes/${encodeURIComponent(path)}/children?${query}`
      )

      const filename = createFileName(files, format)
      const blob = await zip(filename, exportResponseHandlers[format](files))
      download(blob, `${filename}.zip`)

      dispatch(setExportStatus({ ...args, status: 'idle' }))
    } catch (error) {
      dispatch(setExportStatus({ ...args, status: 'error' }))
    }
  }
)

interface PollProps {
  delayMs?: number
  initialDelay?: boolean
  initialDelayMs?: number
  path: NodePath
  provider: ThirdPartyProvider
  userId: UserId
}

export const poll = createAsyncThunk(
  'dataPrivacy/nodes/poll',
  async (
    { delayMs = 4000, initialDelay, initialDelayMs = 4000, path, provider, userId }: PollProps,
    { dispatch, getState }
  ) => {
    const roots = makeSelectNodesRoots(userId)(getState() as RootState)
    !roots.length
      ? dispatch(setStatus({ status: 'loading', userId }))
      : dispatch(setNodeStatus({ path, provider, status: 'loading', userId }))

    initialDelay && (await wait(initialDelayMs))

    try {
      const nodes = await fetchNodes(userId, provider, path)
      if (nodes.length) {
        batch(() => {
          dispatch(addNodes({ nodes, userId }))
          dispatch(updateNodeRoots({ ids: [createNodeId(nodes[0])], userId }))

          // If the main status was previously set, set it back to idle now
          !roots.length && dispatch(setStatus({ status: 'idle', userId }))

          // Set loading for the node
          dispatch(
            setNodeStatus({
              path,
              provider,
              status: nodes[0].loading ? 'loading' : 'idle',
              userId,
            })
          )
        })
      }

      if (!nodes.length || nodes[0].loading) {
        await wait(delayMs)
        await dispatch(poll({ delayMs, path, provider, userId }))
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      !roots.length
        ? dispatch(setStatus({ status: 'error', userId }))
        : dispatch(setNodeStatus({ path, provider, status: 'error', userId }))
    }
  }
)

interface RemovePermissionUsersProps {
  owner: UserId
  path: NodePath
  provider: ThirdPartyProvider
  type: ThirdPartyPermission
  userIds: string[]
}

export const removePermissionUsers = createAsyncThunk<void, RemovePermissionUsersProps>(
  'dataPrivacy/permissions/removeUsers',
  async ({ owner, path, provider, type, userIds }, { dispatch, getState }) => {
    dispatch(setPermissionsStatus({ path, provider, status: 'saving', userId: owner }))

    const currentUserIds = makeSelectPermission(
      owner,
      provider,
      path,
      type
    )(getState() as RootState)
    const newUserIds = currentUserIds.filter((userId) => !userIds.includes(userId))

    try {
      dispatch(
        _setPermission({
          path,
          permissions: { [type]: newUserIds },
          provider,
          userId: owner,
        })
      )

      await updatePermissions(owner, [{ path, permissions: { [type]: newUserIds }, provider }])

      dispatch(setPermissionsStatus({ path, provider, status: 'saved', userId: owner }))
      setTimeout(
        () => dispatch(setPermissionsStatus({ path, provider, status: 'idle', userId: owner })),
        STATUS_CHANGE_DELAY
      )
    } catch (error) {
      dispatch(setPermissionsStatus({ path, provider, status: 'error', userId: owner }))
    }
  }
)

interface RemoveAllPermissionUsersProps {
  owner: UserId
  path: NodePath
  provider: ThirdPartyProvider
  type: ThirdPartyPermission
}

export const removeAllPermissionUsers = createAsyncThunk<void, RemoveAllPermissionUsersProps>(
  'dataPrivacy/permissions/removeAllUsers',
  async ({ owner, path, provider, type }, { dispatch }) => {
    dispatch(setPermissionsStatus({ path, provider, status: 'saving', userId: owner }))

    try {
      dispatch(
        _setPermission({
          path,
          permissions: { [type]: [] },
          provider,
          userId: owner,
        })
      )

      await updatePermissions(owner, [{ path, permissions: { [type]: [] }, provider }])

      dispatch(setPermissionsStatus({ path, provider, status: 'saved', userId: owner }))
      setTimeout(
        () => dispatch(setPermissionsStatus({ path, provider, status: 'idle', userId: owner })),
        STATUS_CHANGE_DELAY
      )
    } catch (error) {
      dispatch(setPermissionsStatus({ path, provider, status: 'error', userId: owner }))
    }
  }
)

let timeout: NodeJS.Timeout

interface SetSearchProps {
  search: string
  userId: UserId
}

export const setSearch = createAsyncThunk(
  'dataPrivacy/search',
  ({ search, userId }: SetSearchProps, { dispatch }) => {
    dispatch(_setSearch({ search, userId }))
    if (timeout) {
      clearTimeout(timeout)
    }

    timeout = setTimeout(async () => {
      dispatch(setStatus({ status: 'loading', userId }))
      try {
        const nodes = await APIClient.makeAPICall(
          'GET',
          `/data-privacy/nodes${!search ? `/${encodeURIComponent('/')}` : ''}?q=${search}`
        )
        batch(() => {
          dispatch(removeNodesExpanded(userId))
          dispatch(addNodes({ nodes, userId }))
          // don't make all root nodes, only the one with the least parents
          dispatch(setNodeRoots({ ids: nodes.map(createNodeId), userId }))
          dispatch(setStatus({ status: 'idle', userId }))
        })
      } catch (error) {
        dispatch(setStatus({ status: 'error', userId }))
      }
    }, 800)
  }
)

interface UploadProps {
  file: File
  path?: NodePath
  provider: ThirdPartyProvider
  userId: UserId
}

export const upload = createAsyncThunk(
  'dataPrivacy/upload',
  async ({ file, path = '/', provider, userId }: UploadProps, { dispatch, getState }) => {
    const nodeExists = makeSelectNodeExists(userId, provider, path)(getState() as RootState)

    const _setStatus = (status: Status, percentComplete?: number) => {
      nodeExists && dispatch(setNodeStatus({ path, provider, status, userId }))
      dispatch(setUploadStatus({ percentComplete, provider, status, userId }))
    }

    try {
      _setStatus('saving', 0)
      const url = await APIClient.makeAPICall(
        'POST',
        `/data-privacy/providers/${provider}/upload`,
        {
          body: { filename: cleanFilename(file.name) },
        }
      )

      await axios.put(url, file, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: ({ lengthComputable, loaded, total }: ProgressEvent) => {
          if (lengthComputable) {
            dispatch(
              setUploadStatus({
                percentComplete: Math.round((loaded * 100) / total),
                provider,
                status: 'saving',
                userId,
              })
            )
          }
        },
      })

      batch(() => {
        _setStatus('saved')
        dispatch(removeNodeExpanded({ provider, path, userId }))
        dispatch(removeNodeChildren({ path, provider, recursive: true, userId }))
      })

      // Begin polling for updated nodes
      dispatch(poll({ initialDelay: true, provider, path, userId }))
      // Wait 5s then set stati back to idle - saved should only be a temporary status
      setTimeout(() => _setStatus('idle'), STATUS_CHANGE_DELAY)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      _setStatus('error')
    }
  }
)

interface UpsertGlobalPermissionProps {
  permissions: Partial<ThirdPartyGlobalPermissionRecord>
  userId: UserId
}

export const upsertGlobalPermission = createAsyncThunk<void, UpsertGlobalPermissionProps>(
  'dataPrivacy/globalPermissions/set',
  async ({ permissions, userId }, { dispatch, getState }) => {
    dispatch(setPermissionsStatus({ path: '/', provider: 'all', status: 'saving', userId }))

    try {
      await dispatch(setGlobalPermission({ permissions, userId }))

      // If there were any downstream permissions that changed then update
      const dirtyPermissions = makeSelectPermissionsDirty(userId)(getState() as RootState)
      const updatedPermission = makeSelectGlobalPermission(userId)(getState() as RootState)

      await updatePermissions<ThirdPartyGlobalPermissionRecord | ThirdPartyPermissionRecord>(
        userId,
        [{ path: '/', permissions: updatedPermission, provider: 'all' }, ...dirtyPermissions]
      )

      dispatch(resetPermissionsDirty(userId))
      dispatch(setPermissionsStatus({ path: '/', provider: 'all', status: 'saved', userId }))
      setTimeout(
        () =>
          dispatch(setPermissionsStatus({ path: '/', provider: 'all', status: 'idle', userId })),
        STATUS_CHANGE_DELAY
      )
    } catch (error) {
      dispatch(setPermissionsStatus({ path: '/', provider: 'all', status: 'error', userId }))
    }
  }
)

interface UpsertPermissionProps {
  owner: UserId
  path: NodePath
  provider?: ThirdPartyProvider
  type: ThirdPartyPermission
  userIds: string[] | null
}

export const upsertPermission = createAsyncThunk(
  'dataPrivacy/permissions/set',
  async (
    { owner, path, provider = 'all', type, userIds }: UpsertPermissionProps,
    { dispatch, getState }
  ) => {
    dispatch(setPermissionsStatus({ path, provider, status: 'saving', userId: owner }))
    // const selectPermission = makeSelectPermission(owner, provider, path, type)

    // const currentUserIds = selectPermission(getState() as RootState)
    const newPermission = { [type]: userIds }

    try {
      await dispatch(
        _setPermission({
          path,
          permissions: newPermission,
          provider,
          userId: owner,
        })
      )

      // If there were any downstream permissions that changed then update
      const dirtyPermissions = makeSelectPermissionsDirty(owner)(getState() as RootState)

      // Update the API
      await updatePermissions(owner, [
        { path, permissions: newPermission, provider },
        ...dirtyPermissions,
      ])

      dispatch(resetPermissionsDirty(owner))
      dispatch(setPermissionsStatus({ path, provider, status: 'saved', userId: owner }))
      setTimeout(
        () => dispatch(setPermissionsStatus({ path, provider, status: 'idle', userId: owner })),
        STATUS_CHANGE_DELAY
      )
    } catch (error) {
      dispatch(setPermissionsStatus({ path, provider, status: 'error', userId: owner }))
    }
  }
)

// Selectors
const selectRoot = (state: RootState) => state.dataPrivacy

// Nodes
const selectNodesRoot = createSelector(selectRoot, ({ nodes }) => nodes)
const makeSelectNodesByUserId = (userId: UserId) =>
  createSelector(selectNodesRoot, (nodes) => nodes[userId] ?? ({} as IDataPrivacyNodeState))
const makeSelectNodesIsEmpty = (userId: UserId) =>
  createSelector(makeSelectNodesByUserId(userId), (nodes) => !!nodes)
const makeSelectNodesById = (userId: UserId) =>
  createSelector(makeSelectNodesByUserId(userId), (nodes) => nodes?.byId ?? {})
const makeSelectNodesRoots = (userId: UserId) =>
  createSelector(makeSelectNodesByUserId(userId), (nodes) => nodes.roots ?? [])
const makeSelectNodesStatus = (userId: UserId) =>
  createSelector(makeSelectNodesByUserId(userId), (nodes) => nodes.status ?? {})
const makeSelectNodesExpanded = (userId: UserId) =>
  createSelector(makeSelectNodesByUserId(userId), (nodes) => nodes.expanded ?? [])
const makeSelectNodesByParent = (userId: UserId) =>
  createSelector(makeSelectNodesByUserId(userId), (nodes) => nodes.byParentNode ?? {})

// Permissions
const selectPermissionsRoot = createSelector(selectRoot, ({ permissions }) => permissions)
export const selectPermissionedUserIds = createSelector(
  selectPermissionsRoot,
  ({ userIds }) => userIds
)

export const selectPermissionsOverallStatus = createSelector(
  selectPermissionsRoot,
  ({ status }) => status
)
const makeSelectPermissionsByUserId = (userId: UserId) =>
  createSelector(selectPermissionsRoot, ({ byUserId }) => byUserId[userId])
const makeSelectPermissionsGlobal = (userId: UserId) =>
  createSelector(selectPermissionsRoot, ({ global }) => global[userId])
const makeSelectPermissionsByNodeId = (userId: UserId) =>
  createSelector(makeSelectPermissionsByUserId(userId), (byUserId) => byUserId?.byNodeId ?? {})
const makeSelectPermissionsSorted = (userId: UserId) =>
  createSelector(makeSelectPermissionsByUserId(userId), (byUserId) => byUserId?.sorted ?? [])
const makeSelectPermissionsStatus = (userId: UserId) =>
  createSelector(selectPermissionsRoot, (permissions) => permissions.byUserId[userId]?.status ?? {})
export const makeSelectPermissionsCounts = (userId: UserId) =>
  createSelector(selectPermissionsRoot, (permissions) => permissions.byUserId[userId]?.counts ?? {})

// Misc
const selectStatusRoot = createSelector(selectRoot, ({ status: status }) => status)
const selectUploadStatusRoot = createSelector(selectRoot, ({ uploadStatus }) => uploadStatus)

export const makeSelectSearch = (userId: UserId) =>
  createSelector(selectRoot, ({ search }) => search[userId] ?? '')

export const makeSelectRootNodes = (userId: UserId) =>
  createSelector(makeSelectNodesById(userId), makeSelectNodesRoots(userId), (byId, roots) =>
    roots.map((id) => byId[id])
  )

export const makeSelectNodeStatus = (
  userId: UserId,
  provider: ThirdPartyProvider,
  path: NodePath
) =>
  createSelector(
    makeSelectNodesStatus(userId),
    (status) => status[createNodeId({ provider, path })]
  )

export const makeSelectNodeExpanded = (
  userId: string,
  provider: ThirdPartyProvider,
  path: NodePath
) =>
  createSelector(makeSelectNodesExpanded(userId), (expanded) =>
    expanded.includes(createNodeId({ provider, path }))
  )

export const makeSelectNode = (userId: UserId, provider: ThirdPartyProvider, path: NodePath) =>
  createSelector(makeSelectNodesById(userId), (byId) => byId[createNodeId({ provider, path })])

export const makeSelectNodeExists = (
  userId: UserId,
  provider: ThirdPartyProvider,
  path: NodePath
) => createSelector(makeSelectNodesById(userId), (byId) => !!byId[createNodeId({ provider, path })])

export const makeSelectChildNodes = (
  userId: UserId,
  provider: ThirdPartyProvider,
  path: NodePath,
  limit: number,
  page: number
) =>
  createSelector(makeSelectNodesById(userId), makeSelectNodesByParent(userId), (byId, byParent) => {
    const parent = byParent[createNodeId({ provider, path })]
    if (!parent) {
      return { count: 0, children: [], pages: 0 }
    }
    const { count, children, pages } = parent
    return {
      count,
      children: children
        .slice((page - 1) * limit, page * limit)
        .filter((c) => c)
        .map((id) => byId[id]),
      pages,
    }
  })

export const makeSelectNodeProviders = (userId: UserId) =>
  createSelector(makeSelectNodesByUserId(userId), ({ providers }) => providers)

const makeSelectPermissionsDirty = (userId: UserId) =>
  createSelector(selectPermissionsRoot, ({ byUserId }) => byUserId[userId]?.dirty)

export const makeSelectGlobalPermissionedUsers = (userId: UserId) =>
  createSelector(makeSelectGlobalPermission(userId), (globalPermissions) =>
    Object.entries(globalPermissions).reduce(
      (acc, [type, userIds]) =>
        userIds.reduce(
          (acc, userId) => ({
            ...acc,
            [userId]: (acc[userId] ?? []).concat(type as ThirdPartyPermission),
          }),
          acc
        ),
      {} as Record<UserId, ThirdPartyPermission[]>
    )
  )

export const makeSelectIsPermissionSet = (
  userId: UserId,
  provider: ThirdPartyProvider,
  path: NodePath,
  type: ThirdPartyPermission
) =>
  createSelector(makeSelectPermissionsByNodeId(userId), (byNodeId) => {
    const permissions = byNodeId[createNodeId({ path, provider })]
    return !!permissions && !!permissions[type]
  })

export const makeSelectGlobalPermission = (userId: UserId) =>
  createSelector(makeSelectPermissionsGlobal(userId), (global) => global)

export const makeSelectPermission = (
  userId: UserId,
  provider: ThirdPartyProvider,
  path: NodePath,
  type: ThirdPartyPermission
) =>
  createSelector(
    makeSelectPermissionsByNodeId(userId),
    makeSelectPermissionsSorted(userId),
    makeSelectPermissionsGlobal(userId),
    (byNodeId, sorted, globalPermissions) => {
      const directNodeId = createNodeId({ path, provider })
      const directPermission = byNodeId[directNodeId] && byNodeId[directNodeId][type]
      const globalGuard = globalPermissionGuards[type]

      if (directPermission) {
        return globalGuard(directPermission, globalPermissions)
      }

      const parentNodeId = sorted.find(
        (nodeId) =>
          directNodeId.startsWith(nodeId) && nodeId !== directNodeId && byNodeId[nodeId][type]
      )
      const parentPermissions = byNodeId[parentNodeId] ?? {}
      return globalGuard(parentPermissions[type], globalPermissions) ?? []
    }
  )

// export const makeSelectPermissionAvailableUserIds = (
//   userId: UserId,
//   path: NodePath,
//   provider: ThirdPartyProvider,
//   permissionType: ThirdPartyPermission
// ) =>
//   createSelector(makeSelectPermissionsByNodeId(userId), selectOtherUserIds, (byNodeId, userIds) => {
//     const directNodeId = createNodeId({ path, provider })

//     const permissionUserIds = Object.entries(byNodeId).reduce(
//       (acc, [nodeId, ancestorPermissions]) => {
//         /**
//          * If this permission isn't the global permission OR this isn't an ancestor of the direct
//          * permission OR this is the direct permission, then don't do anything
//          */
//         if (
//           (!directNodeId.startsWith(nodeId) && nodeId !== GLOBAL_NODE_ID) ||
//           directNodeId === nodeId
//         ) {
//           return acc
//         }

//         return uniq(acc.concat(ancestorPermissions[permissionType] ?? []))
//       },
//       [] as string[]
//     )

//     return difference(userIds, permissionUserIds)
//   })

export const makeSelectPermissionStatus = (
  userId: UserId,
  provider: ThirdPartyProvider,
  path: NodePath
) =>
  createSelector(
    makeSelectPermissionsStatus(userId),
    (status) => status[createNodeId({ provider, path })]
  )

export const makeSelectPermissionCount = (
  userId: UserId,
  provider: ThirdPartyProvider,
  path: NodePath,
  type: ThirdPartyPermission
) =>
  createSelector(makeSelectPermissionsCounts(userId), (counts) => {
    const record = counts[createNodeId({ provider, path })]
    return (record && record[type]) ?? 0
  })

export const makeSelectStatus = (userId: UserId) =>
  createSelector(selectStatusRoot, (status) => status[userId])

const makeSelectUploadStatus = (userId: UserId) =>
  createSelector(selectUploadStatusRoot, (uploadStatus) => uploadStatus[userId])

export const makeSelectUploadStatusStatus = (userId: UserId, provider: ThirdPartyProvider) =>
  createSelector(makeSelectUploadStatus(userId), (uploadStatus) =>
    uploadStatus ? uploadStatus[provider]?.status : undefined
  )

export const makeSelectUploadStatusPercent = (userId: UserId, provider: ThirdPartyProvider) =>
  createSelector(makeSelectUploadStatus(userId), (uploadStatus) =>
    uploadStatus ? uploadStatus[provider]?.percentComplete : undefined
  )

export const makeSelectUploadStatusLeastPercent = (userId: UserId) =>
  createSelector(makeSelectUploadStatus(userId), (uploadStatus) => {
    if (uploadStatus) {
      const values = Object.values(uploadStatus)
      const uploading = values.filter(({ percentComplete }) => percentComplete)
      if (!uploading.length) {
        return undefined
      }

      return uploading.reduce(
        (acc, { percentComplete }) => Math.min(acc, percentComplete),
        uploading[0].percentComplete
      )
    }
  })

export const makeSelectUploadStatusAllPercent = (userId: UserId) =>
  createSelector(makeSelectUploadStatus(userId), (uploadStatus) => {
    if (uploadStatus) {
      return Object.entries(uploadStatus).reduce(
        (acc, [provider, { percentComplete }]) => ({ ...acc, [provider]: percentComplete }),
        {}
      )
    }

    return {}
  })

export const makeSelectIsUploading = (userId: UserId) =>
  createSelector(makeSelectUploadStatus(userId), (uploadStatus) => {
    if (uploadStatus) {
      return Object.values(uploadStatus).reduce(
        (acc, { status }) => acc || ['saving', 'loading'].includes(status),
        false
      )
    }

    return false
  })

export const selectSelectedUserId = createSelector(
  selectRoot,
  ({ selectedUserId }) => selectedUserId
)

const selectExportStatus = createSelector(selectRoot, ({ exportStatus }) => exportStatus)
export const makeSelectExportStatus = (
  userId: UserId,
  provider: ThirdPartyProvider = 'all',
  path: NodePath = '/'
) =>
  createSelector(
    selectExportStatus,
    (exportStatus) => exportStatus[userId + createNodeId({ path, provider })]
  )

// Reducer
export default slice.reducer
